import config from "../config";
import {
  arrayUnique,
  digObject,
  getProp,
  getTreeProp,
  isFunction,
  isObject,
  isPostal,
  jsonParse,
  listSize,
  splitWords,
  strlen,
} from "./util";
import { dropSession } from "./session";
import { buildRequestHeader, buildPropertyPostData } from "./oss-util";
import { getAddresses, transformPropertyData } from "./oss-transform";
import { getHttpClient } from "./api";
import { transformUpdatedFeatures } from "./features-config";
import {
  getAuthToken,
  getPropertyType,
  filterAvailableProducts,
  getGlobal,
  setGlobal,
  getGlobalValue,
  resetApiGlobals,
  setGlobalValue,
  isProductAvailable,
} from "./global-util";
import {
  setAndStoreGlobals,
  setStoredGlobalValue,
  storePropertyData,
  storeRequestedProducts,
} from "./stored-data";
import getLogger from "./debug-logger";

const log = getLogger("oss-api", 1);

const { ossApiEndpoint, ossApiTimeout } = config;

//------------------------------------------------------------
// send OSS POST request
//------------------------------------------------------------
export const ossPost = async (postData, queryString = "") => {
  let error = "",
    message = "",
    res = null,
    dataObject = null,
    qs = strlen(queryString) && !queryString.startsWith("?") ? "?" + queryString : queryString;

  const conf = {
    method: "POST",
    url: ossApiEndpoint + qs,
    timeout: ossApiTimeout,
    validateStatus: null, // resolve error responses
    data: postData,
  };
  //
  const token = getAuthToken();
  if (!token) {
    log("Access token not available"); //------------------- log
    return null;
  }
  conf.headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const client = getHttpClient();
  log(`Sending POST request to ${conf.url}`, "REQUEST DATA >>>", conf.data, "<<< END"); //------ log
  //
  try {
    res = await client.request(conf);
    dataObject = getProp(res, "data");
    error = getProp(dataObject, "error") || "";
    if (!dataObject) {
      log(`No data returned from OSS API call`); //---------------------- log
    }
    const status = getProp(res, "status", 0);
    const statusText = getProp(res, "statusText", "error");
    if (status === 401 || error === "invalid_token") {
      log(`${status} ${statusText}`); //---------------------- log
      message = "Access denied";
      dropSession(2000);
      return { DATA_ERROR: message };
    }
    log("OSS response received", "RESPONSE DATA >>>", dataObject, "<<< END"); //-------- log
    // check for error response with status 200
    message = digObject(dataObject, "ResponseBody", "Result", 0, "Message", 0) || "";
  } catch (x) {
    message = (typeof x === "object" && x.message) || x + "";
    log(`OSS API error: ${message}`); //---------------------- log
    return { DATA_ERROR: message };
  }
  if (message) {
    log(`Response has error: '${message}'`); //---------------------- log
    if (isObject(dataObject)) {
      dataObject.DATA_ERROR = message;
    } else {
      dataObject = { DATA_ERROR: message };
    }
  }
  return dataObject;
};

// Address search
const addressSearch = async (searchTerm) => {
  const product = "AddressSearch";
  if (!isProductAvailable(product)) {
    log(`${product} is not available for this user`); //---------- log
  }
  const addrObj = isPostal(searchTerm) ? { PostalCode: searchTerm } : { StreetAddress: searchTerm };
  const data = {
    RequestHeader: buildRequestHeader(),
    RequestBody: {
      Property: [
        {
          Address: addrObj,
        },
      ],
      RequestedProducts: {
        Product: [product],
      },
    },
  };
  const respData = await ossPost(data);
  return respData;
};

// extract addresses from response data
export const fetchAddresses = async (searchTerm) => {
  const respData = await addressSearch(searchTerm);
  return getAddresses(respData); // array or null
};

//----------------------------------------------------------
// getting OSS products (set propertyAddress to null to use current global address)
//----------------------------------------------------------
export const fetchProducts = async (propertyAddress, prodList, payload = null) => {
  const address =
    propertyAddress || getGlobalValue("ADDRESS_LINE") || getGlobalValue("ADDRESS_REQUESTED");

  if (!listSize(prodList)) {
    log(`none of requested products available for current user`); //----- log
    return null;
  }
  const postData = buildPropertyPostData(address, prodList, payload);
  if (!postData) {
    log(`could not create POST data for address`, address); //-------------- log
    return null;
  }

  const respData = await ossPost(postData);

  if (!isObject(respData)) {
    log(`response data is ${respData}`); //-------------- log
    return null;
  }
  return respData;
};

//----------------------------------------------------------
// load initial property data from cache or API
// actual requested product list depends on user configuration
//----------------------------------------------------------
export const loadPropertyData = async (searchAddr) => {
  resetApiGlobals();

  const revealRange = !!getGlobalValue("REVEAL_EVR");
  const revealAvm = !!getGlobalValue("REVEAL_AVM");

  const prodList = [
    "iClarifyFinancialResidentialPrefill",
    "BuildingImagery",
    "iClarifyFinancialResidentialReport",
    "PropertyListings",
    "PropertySales",
    //
    "AssessmentListings",
    "RentalListings",
    //
    "PropertyStats",
    "NeighbourhoodName",
    "MarketMetrics",
    "HPIHistorical",
    "OptaPermitFinancial",
    revealRange && "EstimatedValueRange",
    revealAvm && "ResidentialMarketValuation",
    revealAvm && "MarketValuationConfidence",
  ].filter(Boolean);

  const callList = filterAvailableProducts(prodList, false); // only available OSS products
  storeRequestedProducts(callList); // track requested products in localstorage

  const respData = await fetchProducts(searchAddr, callList);

  const transformed = transformPropertyData(respData, prodList); // always returns object

  setAndStoreGlobals(transformed);

  // check if prefill data exists, store retrieved data locally
  if (getProp(transformed, "FEATURES_CURRENT")) {
    storePropertyData();
  }
  return transformed;
};

//----------------------------------------------------------
// Updating valuation products, keeping PDF report updated
// with all previous changes.
// This acts as "Save" operation when no products explicitly specified
//----------------------------------------------------------
const updatePropertyProducts = async (prodList) => {
  // reset error messages
  resetApiGlobals();

  const hasReport = prodList.includes("iClarifyFinancialResidentialReport");
  const hasSnapshot = prodList.includes("iClarifySnapshotReport");

  // drop existing report, if new one requested
  hasReport && setGlobalValue("REPORT_URL", null);

  const requestedProducts = getGlobalValue("REQUESTED_PRODUCTS") || [];

  // if Report is requested include all products that have already been pulled in:
  // those additional products won't be included in data transformation
  let actualProdList = hasReport ? [...prodList, ...requestedProducts] : [...prodList];
  if (hasSnapshot) {
    actualProdList = [
      ...actualProdList,
      "EstimatedValueRange",
      "NeighbourhoodName",
      "MarketMetrics",
    ];
  }

  actualProdList = filterAvailableProducts(arrayUnique(actualProdList));

  if (!actualProdList) {
    log(`transformed product list is empty`); //------------------------ log
    return null;
  }

  // in case Report is not configured for the user
  if (hasReport && !actualProdList.includes("iClarifyFinancialResidentialReport")) {
    log(`PDF Report is not allowed for this user, check configutation!`); //------------- log
    return null;
  }

  log(`updating products:`, actualProdList); //------------------- log

  // build request payload
  const refObj = jsonParse(getGlobalValue("FEATURES_INITIAL")) || {}; // original data object
  // separate construction feats from stats, if added

  const propType = getPropertyType(); // current property type

  // list of missing data entries, each one populated with empty string ''
  const missingDataKeys = splitWords(getGlobalValue("FEATURES_MISSING"));

  // restore or collect updated data points
  let updatedFeats = jsonParse(getGlobalValue("FEATURES_UPDATED")) || null;
  if (!updatedFeats) {
    updatedFeats = transformUpdatedFeatures(getGlobal(), refObj, propType, missingDataKeys);
    setStoredGlobalValue("FEATURES_UPDATED", JSON.stringify(updatedFeats));
  }
  log(`updated features to be sent in payload`, updatedFeats); //------------------ log
  let payload = { ...updatedFeats };

  if (actualProdList.includes("RentalValuation")) {
    payload = {
      ...(payload || {}),
      RentalInformation: {
        RentalUnitType: getGlobalValue("RENTAL_TYPE") || null,
      },
    };
  }
  // add updated stats on Report calls, if present
  if (hasReport) {
    const updStatsJson = getGlobalValue("STATS_CURRENT") || getGlobalValue("STATS_UPDATED");
    if (updStatsJson) {
      payload = {
        ...(payload || {}),
        PropertyStats: [jsonParse(updStatsJson)],
      };
    }
  }
  const [addrLine, addrObj] = [getGlobalValue("ADDRESS_LINE"), getGlobalValue("ADDRESS_REQUESTED")];
  const addr = addrLine || addrObj;

  log(`fetching product updates`); //----------------- log
  const respData = await fetchProducts(addr, actualProdList, payload);
  const transformed = isObject(respData)
    ? transformPropertyData(respData, prodList)
    : { ERROR: "No data" }; // transformed property data

  // if Report was requested but not returned, set it to ''
  if (hasReport && !getProp(transformed, "REPORT_URL")) {
    transformed.REPORT_URL = "";
  }
  setAndStoreGlobals(transformed);

  storeRequestedProducts(prodList);

  log("updated and saved global entries:", Object.keys(transformed).sort()); //------------ log
  return transformed;
};

//----------------------------------------------------------
// Update PDF Report URL
//----------------------------------------------------------
export const updateReportUrl = async () => {
  log(`updating PDF Report URL`); //--------------------------- log
  const data = await updatePropertyProducts(["iClarifyFinancialResidentialReport"]);
  return data;
};

//----------------------------------------------------------
// load initial Market Value
//----------------------------------------------------------
export const loadMarketValue = async () => {
  log(`fetching Market Value`); //--------------------------- log
  const data = await updatePropertyProducts([
    "ResidentialMarketValuation",
    "MarketValuationConfidence",
    "iClarifyFinancialResidentialReport",
  ]);
  return data;
};

//----------------------------------------------------------
// load initial Estimated Value Range
//----------------------------------------------------------
export const loadEstimatedValueRange = async () => {
  log(`fetching Estimated Value Range`); //--------------------------- log
  const data = await updatePropertyProducts([
    "EstimatedValueRange",
    "MarketValuationConfidence",
    "iClarifyFinancialResidentialReport",
  ]);
  return data;
};

//----------------------------------------------------------
// load Rental Value for selected property type
//----------------------------------------------------------
export const loadRentalValue = async () => {
  log(`fetching Rental Value`); //--------------------------- log
  const data = await updatePropertyProducts([
    "RentalValuation",
    "MarketValuationConfidence",
    "iClarifyFinancialResidentialReport",
  ]);
  return data;
};

//----------------------------------------------------------
// refresh current property images when using cached property info
//----------------------------------------------------------
export const loadImages = async () => {
  log(`loading property imagery from API`); //-------------- log
  const prodList = ["BuildingImagery"];
  const respData = await fetchProducts(null, prodList); // use current address
  if (respData) {
    const data = transformPropertyData(respData, prodList);
    setGlobal(data);
    return data;
  }
  return null;
};

//----------------------------------------------------------
// Update PDF Snapshot URL
//----------------------------------------------------------
export const loadSnapshotReport = async (callback = null) => {
  const f = isFunction(callback) ? callback : () => {}; // optional callback function
  await updatePropertyProducts(["iClarifySnapshotReport"]);
  f();
};
